import { Field, useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { requiredValidation, validateRequiredEndDate, validateRequiredEndTime } from "../../../../utilities/Validation";
import DateField from "../../../date-field/date-field";
import Label from "../../../label/label";
import PaginatedSearchableSelectField from "../../../selection-field/paginated-searchable-select-field";
import TimeField from "../../../timeField/timeField";
import { getVingCardTimeScheduleNamesWithPagination } from "../../../../services/vingCardService";
import SettingSelectField from "../../../settingFields/SettingSelectField";

interface DetailFormProps {
    onNext: (event: any) => void;
    onBack: (event: any) => void;
    vingCardType: "staff" | "guest";
}

const DetailForm: FC<DetailFormProps> = ({ onNext, onBack, vingCardType }) => {
    const { translate } = useLocale();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const isFormValid = Object.keys(errors).length === 0 && (vingCardType === "guest" && !!values.roomNumber) || (vingCardType === "staff" && !!values.timeScheduleId);

    return <div className="flex flex-col h-full space-y-2 relative">
        <div className={` text-portal-blue-primary-shade2 text-xl font-semibold`} >
            {translate.byKey(TranslationKeys.captureDetails)}
        </div>
        {/* Room Number */}
        {vingCardType === "guest" && <div className="flex flex-col w-1/2">
            <div className="flex flex-row justify-between">
                <Label
                    htmlFor="roomNumber"
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.roomNo)}</Label>
                {errors.roomNumber ? <span className='text-red-600 text-xs'>{errors.roomNumber}</span> : null}
            </div>
            <SettingSelectField
                accessor={`roomNumber`}
                options={values.selectedDoors ? values.selectedDoors.map((door) => {
                    return { label: door, value: door }
                }) : []} />
        </div>}

        {/* Check-in */}
        {vingCardType === "guest" && <div className="flex flex-row">
            <div className="flex flex-col flex-1 mr-2">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="checkInDate"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.checkInDate)}:</Label>
                    {errors.checkInDate ? <span className='text-red-600 text-xs'>{errors.checkInDate}</span> : null}
                </div>
                <div className="flex flex-row justify-between" id="checkInDate">
                    <Field
                        type="text"
                        valueName="checkInDate"
                        name="checkInDate"
                        component={DateField}
                        validate={(values) => requiredValidation(values, translate)} />
                </div>
            </div>
            <div className="flex flex-col flex-1">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="checkInTime"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.checkInTime)}:</Label>
                    {errors.checkInTime ? <span className='text-red-600 text-xs'>{errors.checkInTime}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="checkInTime"
                        valueName="checkInTime"
                        component={TimeField}
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
            </div>
        </div>}

        {/* Check-out */}
        {vingCardType === "guest" && <div className="flex flex-row">
            <div className="flex flex-col flex-1 mr-2">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="checkOutDate"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.checkOutDate)}:</Label>
                    {errors.checkOutDate ? <span className='text-red-600 text-xs'>{errors.checkOutDate}</span> : null}
                </div>
                <div className="flex flex-row justify-between" id="checkOutDate">
                    <Field
                        type="text"
                        valueName="checkOutDate"
                        name="checkOutDate"
                        component={DateField}
                        validate={(value) => validateRequiredEndDate(values.checkOutDate, values.checkInDate, translate)} />
                </div>
            </div>
            <div className="flex flex-col flex-1">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="checkOutTime"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.checkOutTime)}:</Label>
                    {errors.checkOutTime ? <span className='text-red-600 text-xs'>{errors.checkOutTime}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="checkOutTime"
                        valueName="checkOutTime"
                        component={TimeField}
                        validate={(value) => validateRequiredEndTime(values.checkInTime, value, translate)} />
                </div>
            </div>
        </div>}

        {/* Time Schedule */}
        {vingCardType === "staff" && <div className="flex flex-col w-1/2">
            <div className="flex flex-row justify-between">
                <Label htmlFor="timeScheduleId" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.timeSchedule)}:</Label>
                {errors.timeScheduleId ? <span className='text-red-600 text-xs'>{errors.timeScheduleId}</span> : null}
            </div>

            <PaginatedSearchableSelectField
                name={translate.byKey(TranslationKeys.timeSchedule)}
                key={"timeScheduleId"}
                accessor='timeScheduleId'
                dataMethod={getVingCardTimeScheduleNamesWithPagination}
                searchAccessor={'timeSchedule'}
                onSelect={(value, item) => {
                    console.log(value)
                    setFieldValue("timeScheduleId", value)
                    setFieldValue("timeSchedule", item.label)
                }} />
        </div>}

        {/* User expiration date */}
        <div className="flex flex-row">
            <div className="flex flex-col flex-1 mr-2">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="userExpirationDate"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.userExpirationDate)}:</Label>
                    {errors.userExpirationDate ? <span className='text-red-600 text-xs'>{errors.userExpirationDate}</span> : null}
                </div>
                <div className="flex flex-row justify-between" id="userExpirationDate">
                    <Field
                        type="text"
                        valueName="userExpirationDate"
                        name="userExpirationDate"
                        component={DateField}
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
            </div>
            <div className="flex flex-col flex-1">
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="userExpirationTime"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.userExpirationTime)}:</Label>
                    {errors.userExpirationTime ? <span className='text-red-600 text-xs'>{errors.userExpirationTime}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="userExpirationTime"
                        valueName="userExpirationTime"
                        component={TimeField}
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
            </div>
        </div>

        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className={`secondary secondary-inverse py-1 px-5`}
                    type="button"
                    onClick={onBack}>{translate.byKey(TranslationKeys.back)}</button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    id="nextButton"
                    data-cy="nextButton"
                    className={`py-1 px-5 secondary ${isFormValid ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    disabled={!isFormValid}
                    onClick={onNext}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>
}

export default DetailForm;
